import { z } from 'zod';

import {
  crvLimitIdentifierTypeValues,
  crvLimitModeValues,
  type CreateCrvLimitLedgerEntryRequest,
  type CreateCrvLimitLedgerEntryResponse,
  type CrvLimitCheckRequest,
  type CrvLimitCheckResponse,
  type CrvLimitIdentifierType,
  type CrvLimitLedgerEntryDto,
  type CrvLimitMode,
} from './materials-catalog-limits.dto.js';

const timestampSchema = z.iso.datetime();
const rateSchema = z.number().min(0);
const identifierValueSchema = z.string().trim().min(1);
const limitSchema = z.number().min(0);

export const CrvLimitIdentifierTypeSchema: z.ZodType<CrvLimitIdentifierType> = z.enum(crvLimitIdentifierTypeValues);
export const CrvLimitModeSchema: z.ZodType<CrvLimitMode> = z.enum(crvLimitModeValues);

export const CrvLimitLedgerEntryDtoSchema: z.ZodType<CrvLimitLedgerEntryDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    operatorUserId: z.uuid(),
    materialId: z.uuid(),
    identifierType: CrvLimitIdentifierTypeSchema,
    identifierValue: identifierValueSchema,
    occurredAt: timestampSchema,
    mode: CrvLimitModeSchema,
    totalCrvPaid: rateSchema,
    swRatePerLb: rateSchema,
    equivalentLbs: rateSchema,
    isReversal: z.boolean(),
    reversalOfEntryId: z.uuid().nullable(),
    purchaseId: z.uuid().nullable(),
    createdAt: timestampSchema,
  })
  .superRefine((value, ctx) => {
    if (value.isReversal && !value.reversalOfEntryId) {
      ctx.addIssue({
        code: 'custom',
        path: ['reversalOfEntryId'],
        message: 'Reversal entries must include reversalOfEntryId.',
      });
    }
    if (!value.isReversal && value.reversalOfEntryId) {
      ctx.addIssue({
        code: 'custom',
        path: ['reversalOfEntryId'],
        message: 'Non-reversal entries cannot include reversalOfEntryId.',
      });
    }
  });

export const CreateCrvLimitLedgerEntryRequestSchema: z.ZodType<CreateCrvLimitLedgerEntryRequest> = z
  .object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    operatorUserId: z.uuid(),
    materialId: z.uuid(),
    identifierType: CrvLimitIdentifierTypeSchema,
    identifierValue: identifierValueSchema,
    occurredAt: timestampSchema,
    mode: CrvLimitModeSchema,
    totalCrvPaid: rateSchema,
    swRatePerLb: rateSchema,
    equivalentLbs: rateSchema,
    isReversal: z.boolean().optional(),
    reversalOfEntryId: z.uuid().nullable().optional(),
    purchaseId: z.uuid().nullable().optional(),
  })
  .superRefine((value, ctx) => {
    const isReversal = value.isReversal ?? false;
    const reversalOfEntryId = value.reversalOfEntryId ?? null;
    if (isReversal && !reversalOfEntryId) {
      ctx.addIssue({
        code: 'custom',
        path: ['reversalOfEntryId'],
        message: 'Reversal entries must include reversalOfEntryId.',
      });
    }
    if (!isReversal && reversalOfEntryId) {
      ctx.addIssue({
        code: 'custom',
        path: ['reversalOfEntryId'],
        message: 'Non-reversal entries cannot include reversalOfEntryId.',
      });
    }
  });

export const CreateCrvLimitLedgerEntryResponseSchema: z.ZodType<CreateCrvLimitLedgerEntryResponse> = z.object({
  entry: CrvLimitLedgerEntryDtoSchema,
});

export const CrvLimitCheckRequestSchema: z.ZodType<CrvLimitCheckRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  materialId: z.uuid(),
  identifierType: CrvLimitIdentifierTypeSchema,
  identifierValue: identifierValueSchema,
  occurredAt: timestampSchema,
  swRatePerLb: rateSchema,
  pendingCrvPaid: rateSchema,
});

export const CrvLimitCheckResponseSchema: z.ZodType<CrvLimitCheckResponse> = z.object({
  isAllowed: z.boolean(),
  limitLbs: limitSchema.nullable(),
  usedEquivalentLbs: limitSchema.nullable(),
  requestedEquivalentLbs: limitSchema.nullable(),
  remainingEquivalentLbs: limitSchema.nullable(),
});
